import { doesQuoteRequirePrinter, isQuoteDropOff } from './quotes';
import { BasketItem, BasketQuote, OrderValidation, QuoteAddress, QuoteExtras } from './types/basket-types';
import { BasketSections } from './types/enums';

type AnalyticsItem = {
  item_id: string;
  item_name: string;
  item_brand: string;
  price: number;
  item_category: string;
  item_category2?: string;
  item_category3?: string;
};

function mapExtras(extras: QuoteExtras, quote: BasketQuote): AnalyticsItem[] {
  return extras.extras
    .map((extra) => {
      const e = quote.availableExtras.find((x) => x.key === extra.key);
      if (!e) return null;

      return {
        item_id: extra.key,
        item_name: extra.key,
        item_brand: 'Parcel2Go',
        price: e.price.net / 100,
        item_category: 'Extra',
        item_category2: undefined,
        item_category3: undefined
      };
    })
    .filter((x) => x !== null);
}

function mapQuote(quote: BasketQuote): AnalyticsItem {
  return {
    item_id: quote.service.slug,
    item_name: quote.service.name,
    item_brand: quote.service.courier,
    price: quote.price.net / 100,
    item_category: isQuoteDropOff(quote) ? 'Drop Off' : 'Collection',
    item_category2: doesQuoteRequirePrinter(quote) ? 'Printer Required' : 'Printer Not Required',
    item_category3: quote.metadata?.Group as string
  };
}

function mapItems(basketItems: { name: string; item: BasketItem }[]) {
  return basketItems.flatMap((item) => {
    const quote = item.item.sections[BasketSections.QUOTE]?.payload as BasketQuote;
    const extras = item.item.sections[BasketSections.EXTRAS]?.payload as QuoteExtras;
    const items = [mapQuote(quote)];
    if (extras) {
      items.push(...mapExtras(extras, quote));
    }
    return items;
  });
}

export function basketToViewCheckoutEvent(
  basketItems: { name: string; item: BasketItem }[],
  breakdown: OrderValidation | null | undefined
) {
  return {
    ecommerce: {
      currency: (basketItems[0].item.sections[BasketSections.QUOTE]?.payload as BasketQuote).currency.code,
      value: (breakdown?.total?.net ?? 0) / 100,
      items: mapItems(basketItems)
    }
  };
}

export function basketToBeginCheckoutEvent(
  basketItems: { name: string; item: BasketItem }[],
  breakdown: OrderValidation | null | undefined,
  discount: string | null | undefined
) {
  return {
    ecommerce: {
      currency: (basketItems[0].item.sections[BasketSections.QUOTE]?.payload as BasketQuote).currency.code,
      value: (breakdown?.total?.net ?? 0) / 100,
      coupon: discount,
      items: mapItems(basketItems)
    }
  };
}

export function basketItemToRemoveItemEvent(item: BasketItem) {
  const quote = item.sections[BasketSections.QUOTE]?.payload as BasketQuote;
  return {
    ecommerce: {
      currency: quote.currency.code,
      value: quote.price.net / 100,
      items: [mapQuote(quote)]
    }
  };
}

export function quoteToAddToBasketEvent(
  origin: QuoteAddress,
  destination: QuoteAddress,
  quote: BasketQuote,
  extras: QuoteExtras
) {
  return {
    ecommerce: {
      currency: quote.currency.code,
      value: quote.price.net / 100,
      origin_country: origin.country,
      destination_country: destination.country,
      items: [mapQuote(quote), ...mapExtras(extras, quote)]
    }
  };
}

export function getCookieValues(cookieNames: string[]): { [key: string]: string } {
  if (!document.cookie) return {};
  try {
    return document.cookie.split(';').reduce((acc, cookie) => {
      const key = cookie.substring(0, cookie.indexOf('=')).trim();
      const value = cookie.substring(cookie.indexOf('=') + 1);
      if (!cookieNames.includes(key)) return acc;
      return { ...acc, [key.replace(/^_/, '')]: value.trim() };
    }, {});
  } catch (e) {
    return {};
  }
}

export function parseAbTastyCookie(): { [key: string]: string } {
  const cookie = getCookieValues(['ABTasty']);
  if (!cookie.ABTasty) return {};
  try {
    const vals = new URLSearchParams(cookie.ABTasty);
    const th = vals.get('th');
    if (!th) return {};
    const tests = th.split('_');
    return tests.reduce((acc, test) => {
      const [key, value] = test.split('.');
      return { ...acc, [`test_${key}`]: value };
    }, {});
  } catch (e) {
    return {};
  }
}
